<template>
  <component
    :is="select ? 'div': 'fieldset'"
    :class="{
      'flex flex-col gap-1': !inline,
      'flex gap-2 items-center justify-center': inline,
    }"
  >
    <div v-if="label || help">
      <label class="block dark:text-gray-400" :for="id">
        {{ label }}
      </label>

      <p v-if="help" :id="aria['aria-describedby']" class="help text-sm text-gray-500">
        {{ help }}
      </p>
    </div>

    <select
      v-if="select"
      v-model="model"
      :class="['l-select', { 'l-select-sm': size === 'sm' }]"
      v-bind="{ id, name, disabled, multiple, required }"
      @input.stop
      @update:model-value="$emit('input', model)"
    >
      <option
        v-for="[ value, text, disabledValue ] in items"
        :key="value"
        :value="value"
        :disabled="disabledValue || disabledValues.includes(value) "
      >
        {{ text }}
      </option>
      <slot/>
    </select>

    <div
      v-else-if="expanded"
      :class="{
        'flex flex-col gap-1': !inline,
        'flex gap-2': inline,
      }"
    >
      <label
        v-for="[value, text] in items"
        :key="value"
        :class="['flex gap-2 items-center', { 'cursor-not-allowed opacity-50': disabled }]"
      >
        <slot v-if="slots.item" name="item" v-bind="{ name, model, text, value, disabled }"/>

        <template v-else>
          <input
            v-model="model"
            class="inline-block checked:bg-blue disabled:opacity-100"
            :name="name"
            :disabled="disabled"
            :value="value"
            :type="multiple ? 'checkbox' : 'radio'"
          >
          {{ text }}
        </template>
      </label>
    </div>
  </component>
</template>

<script setup>
import { computed, useSlots } from "vue"
import { processItems } from "@/selects"
import { uniqueId } from "lodash-es"

const model = defineModel({ type: [Boolean, Number, String, Object], default: null })

const props = defineProps({
  id: { type: String, default: () => `input-${uniqueId()}` },
  name: String,
  label: String,
  help: String,
  placeholder: String,
  items: Array,
  disabled: Boolean,
  expanded: Boolean,
  inline: Boolean,
  multiple: Boolean,
  required: Boolean,
  size: { type: String, default: "md" },
  disabledValues: { type: Array, default: () => ([]) },
  hideLabel: { type: Boolean },
})

defineEmits(["input"])

const slots = useSlots()

const name = computed(() => props.name ?? uniqueId())

const items = computed(() => {
  if (props.expanded && props.placeholder) {
    return [...processItems(props.items), [null, props.placeholder]]
  }

  if (props.placeholder) {
    return [[null, props.placeholder], ...processItems(props.items)]
  }

  return processItems(props.items)
})

const select = computed(() => !props.expanded)

const aria = computed(() => {
  const attrs = {}

  if (props.help || slots.help) {
    attrs["aria-describedby"] = `${props.id}_help`
  }

  return attrs
})
</script>
